import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { addQueryWithReplace, QueryVal, removeQuery } from 'Helpers/Query';
import fr from 'facet-rest';

const AgentSchedulerStateContext = React.createContext(undefined);
const AgentSchedulerDispatchContext = React.createContext(undefined);

const fetchDataFromLinkQueryParam = async (dispatch) => {
  dispatch({ type: 'loadingStart' });
  try {
    const link = QueryVal('link');
    const fetchedCollection = await fr.http.get(link);
    const type = fetchedCollection.meta().type();
    switch (type) {
      case 'availabilityPeriod': {
        const availabilityPeriodResource = fetchedCollection.resources().head();
        const schedulerAgentCollection = await availabilityPeriodResource.relationship(fr.relationships.schedulerAgents.name)?.link('related')
          .get();
        const availabilityResourceLinkQueryParams = new URLSearchParams(availabilityPeriodResource.link('self').url());
        const selectedAgent = availabilityResourceLinkQueryParams.get('leadPlannerAgentId');
        dispatch({
          type: 'setView',
          view: 'month',
          newCollection: fetchedCollection,
          selectedAgent,
          schedulerAgentCollection,
        });
        const weeklyMeetingCountCollection = await availabilityPeriodResource.relationship(fr.relationships.weekMeetingCount.name)?.link('related').get();
        dispatch({ type: 'setWeeklyMeetingCount', weeklyMeetingCountCollection });
        break;
      }
      case 'availability': {
        dispatch({
          type: 'setView', view: 'week', newCollection: fetchedCollection,
        });
        break;
      }
      case 'calendarEvent': {
        const meetingBooked = QueryVal('meetingBooked');
        if (meetingBooked === 'true') {
          dispatch({
            type: 'setView', view: 'nextMeeting', newCollection: fetchedCollection,
          });
        } else {
          dispatch({
            type: 'setView', view: 'newMeeting', newCollection: fetchedCollection,
          });
        }
        break;
      }
      default:
        dispatch({
          type: 'setView', view: null, newCollection: null, selectedAgent: null, schedulerAgentCollection: null,
        });
    }
  } catch (e) {
    dispatch({ type: 'setResponseError', responseErrors: e.errors });
  } finally {
    dispatch({ type: 'loadingEnd' });
  }
};

const fetchProjectedMeetingFromQueryParam = async (dispatch, projectedMeetingLink) => {
  const fetchedCollection = await fr.http.get(projectedMeetingLink);
  dispatch({ type: 'setProjectedMeeting', projectedMeetingCollection: fetchedCollection });
};

const setNewLinkQueryParam = (dispatch, link) => {
  addQueryWithReplace({ link });
};

function init() {
  return {
    view: null,
    currentCollection: null,
    schedulerAgentCollection: null,
    loading: false,
    responseErrors: null,
    projectedMeetingCollection: null,
    weeklyMeetingCountCollection: null,
    selectedDate: null,
    selectedGapStart: QueryVal('selectedGapStart'),
    selectedGapEnd: QueryVal('selectedGapEnd'),
    selectedMeetingDuration: QueryVal('selectedMeetingDuration'),
    selectedBufferDuration: QueryVal('selectedBufferDuration'),
    selectedAgent: QueryVal('leadPlannerAgentId'),
    isAvailable: QueryVal('isAvailable'),
    prevLink: QueryVal('prevLink'),
    meetingBooked: QueryVal('meetingBooked'),
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'setSelectedDate': {
      return {
        ...state,
        selectedDate: action.selectedDate,
      };
    }
    case 'setWeeklyMeetingCount': {
      return {
        ...state,
        weeklyMeetingCountCollection: action.weeklyMeetingCountCollection,
      };
    }
    case 'setProjectedMeeting': {
      return {
        ...state,
        projectedMeetingCollection: action.projectedMeetingCollection,
      };
    }
    case 'setResponseError': {
      return {
        ...state,
        responseErrors: action.responseErrors,
        view: 'error',
      };
    }
    case 'setView': {
      return {
        ...state,
        view: action.view,
        currentCollection: action.newCollection,
        selectedAgent: action.selectedAgent,
        schedulerAgentCollection: action.schedulerAgentCollection,
      };
    }
    case 'loadingStart': {
      return {
        ...state,
        loading: true,
        view: null,
      };
    }
    case 'loadingEnd': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'setNewMeeting': {
      const {
        selectedGapStart,
        selectedGapEnd,
        selectedMeetingDuration,
        selectedBufferDuration,
        isAvailable,
      } = action;
      addQueryWithReplace(
        { selectedGapStart },
        { selectedGapEnd },
        { selectedMeetingDuration },
        { selectedBufferDuration },
        { isAvailable },
      );
      return {
        ...state,
        selectedGapStart,
        selectedGapEnd,
        selectedMeetingDuration,
        selectedBufferDuration,
        isAvailable,
      };
    }
    case 'setPrevLink': {
      const { prevLink } = action;
      addQueryWithReplace({ prevLink });
      return {
        ...state,
        prevLink,
      };
    }
    case 'setMeetingBooked': {
      addQueryWithReplace({ meetingBooked: true });
      return {
        ...state,
        meetingBooked: true,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
function AgentSchedulerProvider({ children }) {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  // this effect fetches and sets the view on initial load
  useEffect(() => {
    fetchDataFromLinkQueryParam(dispatch);
    const projectedMeetingLink = QueryVal('projectedMeetingLink');
    if (projectedMeetingLink) {
      fetchProjectedMeetingFromQueryParam(dispatch, projectedMeetingLink);
    }
  }, []);

  return (
    <AgentSchedulerStateContext.Provider value={state}>
      <AgentSchedulerDispatchContext.Provider value={dispatch}>
        {children}
      </AgentSchedulerDispatchContext.Provider>
    </AgentSchedulerStateContext.Provider>
  );
}

function useAgentSchedulerStateContext() {
  const context = React.useContext(AgentSchedulerStateContext);

  if (context === undefined) {
    throw new Error('useAgentSchedulerStateContext must be used within a AgentSchedulerProvider');
  }

  return context;
}

function useAgentSchedulerDispatchContext() {
  const context = React.useContext(AgentSchedulerDispatchContext);

  if (context === undefined) {
    throw new Error('useAgentSchedulerDispatchContext must be used within a AgentSchedulerProvider');
  }

  return context;
}

AgentSchedulerProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

const closeSchedulerModal = () => {
  removeQuery(
    'modal',
    'link',
    'projectedMeetingLink',
    'selectedGapStart',
    'selectedGapEnd',
    'selectedMeetingDuration',
    'selectedBufferDuration',
    'prevLink',
    'meetingBooked',
    'isAvailable',
    'leadPlannerAgentId',
  );
};

export {
  AgentSchedulerProvider,
  useAgentSchedulerStateContext,
  useAgentSchedulerDispatchContext,
  closeSchedulerModal,
  setNewLinkQueryParam,
  fetchDataFromLinkQueryParam,
  fetchProjectedMeetingFromQueryParam,
};
